import DateFnsCalculator from "./DateFns/DateFnsCalculator"
import DateFnsFormatter from "./DateFns/DateFnsFormatter"
import DateFnsParser from "./DateFns/DateFnsParser"

const dateFormatter = new DateFnsFormatter()
const dateCalculator = new DateFnsCalculator()
const dateParser = new DateFnsParser()

/**
 * This Date Library is a wrapper so that we can change the underlying date library without having to
 * modify the rest of the codebase.
 *
 * The goal here is to have reusable/composable functions on the Interfaces that can be reused.
 *
 * E.g.
 *
 * IDateCalculator
 * ===============
 * addDays - Returns a date with the given number of days added.
 * addSeconds - Returns a date with the given number of seconds added.
 * addMinutes - Returns a date with the given number of minutes added.
 * differenceInDays - Returns the difference in days between two dates.
 * differenceInSeconds - Returns the difference in seconds between two dates.
 * differenceInMilliseconds - Returns the difference in milliseconds between two dates.
 *
 * We can extend the Date calculator to support other date libraries like Moment.js, Luxon, etc.
 *
 * This enables us tp move away from Moment JS into Date-FNS with tested code that should reduce risk of introducing issues.
 * Make sure to add a test scenario for whatever you are replacing, that compares Date-FNS with Moment.js. Once we have removed all
 * Moment JS code, we can remove it and it's tests.
 */

export function transactionDeadline(date = new Date(), defaultMinutes = 20) {
  const deadline = dateCalculator.addMinutes(date, defaultMinutes)
  return dateParser.getUnixTime(deadline)
}

export function fromUnixTime(unixTimestamp: number) {
  return dateParser.fromUnixTime(unixTimestamp)
}

export function secondsUntilNextEpoch(date = new Date()) {
  return dateCalculator.secondsUntilNextEpoch(date)
}

export function formatCountdownTimer(seconds: number) {
  return dateFormatter.formatCountdownTimer(seconds)
}

export function format(date: number | Date, formatString: string) {
  return dateFormatter.format(date, formatString)
}

export function currentEpochNumber({ date, chainSlug }: { date?: Date; chainSlug: string }) {
  return dateCalculator.currentEpochNumber({ date, chainSlug })
}

export default {
  transactionDeadline,
  currentEpochNumber,
  formatCountdownTimer,
  fromUnixTime,
  secondsUntilNextEpoch,
}

// @deprecated
export function getDaysDifferenceFromUnix(unixTimestamp: number, date = new Date()) {
  const date1 = dateParser.fromUnixTime(unixTimestamp)
  return dateCalculator.differenceInDays(date1, date)
}

// @deprecated
export function addDaysAndFormat(days: number, date = new Date()) {
  const newDate = dateCalculator.addDays(date, Math.round(days))
  return dateFormatter.format(newDate, "yyyy-MM-dd")
}

// @deprecated
export function getSecondsDifferenceWithExtraDay(selectedDateString: string, date = new Date()) {
  const selectedDate = dateParser.parse(selectedDateString, "yyyy-MM-dd", new Date())
  const expiry = dateCalculator.addDays(selectedDate, 1)
  return dateCalculator.differenceInSeconds(expiry, date)
}

// @deprecated
export function getDifferenceInSeconds(expiry: Date, date = new Date()) {
  return dateCalculator.differenceInSeconds(expiry, date)
}

// @deprecated
export function getDaysDifference(selectedDateString: string, date = new Date()) {
  const selectedDate = dateParser.parse(selectedDateString, "yyyy-MM-dd", new Date())
  return dateCalculator.differenceInDays(selectedDate, date)
}

// @deprecated
export function getUnixTimeFromDate(date = new Date()) {
  return dateParser.getUnixTime(date)
}

// @deprecated
export function getDateFromUnixTime(unixTimestamp: number) {
  return dateParser.fromUnixTime(unixTimestamp)
}

// @deprecated
export function distanceToNowFromUnix(unixTimestamp: number) {
  return dateFormatter.formatDistanceToNow(new Date(unixTimestamp * 1000), {
    addSuffix: true,
  })
}

// @deprecated
export function differenceInMilliseconds(date1: Date, date2: Date) {
  return dateCalculator.differenceInMilliseconds(date1, date2)
}

// @deprecated
export function addSeconds(date: Date, seconds: number) {
  return dateCalculator.addSeconds(date, seconds)
}

// @deprecated
export function addSecondsToUnix(unixTimestamp: number, seconds: number) {
  const date = new Date(unixTimestamp * 1000)
  const newDate = dateCalculator.addSeconds(date, seconds)
  return dateParser.getUnixTime(newDate)
}

// @deprecated
export function addSecondsAndFormat(date: Date, seconds: number) {
  const newDate = dateCalculator.addSeconds(date, seconds)
  return dateFormatter.format(newDate, "yyyy-MM-dd")
}

export * from "./interfaces"
